import { ref } from 'vue';
import {Group} from '@/types/Groups';
import { sendGraphQLQuery } from '@/services/gql-util';

export function useGroups() {
	const groupTree = ref<Group[]>([]);
	const folderSort = ref<string>('name'); // Default sort order
	const folderOrder = ref<string>('asc'); // Default order field

	/**
	 * Fetches the group tree from the server and updates the reactive state.
	 * @param combine Optional parameter to include additional data.
	 */
	const getSimpleGroupTree = async (vhost:string): Promise<void> => {
		const query = `
		query ($vhost: Int, $parentGroup: Int) {
			Groups {
				groupSimpleTree(vhost: $vhost, parentGroup: $parentGroup) {
					id name description treeleft treeright vhost
					mediaCount memberCount moderationStatus parentGroup
					children { id name description treeleft treeright vhost mediaCount memberCount moderationStatus parentGroup }
				}
			}
		}`;
		try {
			const data = await sendGraphQLQuery(query, {
				vhost: +vhost,
				parentGroup: 0,
			});

			if (data?.Groups?.groupSimpleTree) {
				groupTree.value = [...data.Groups.groupSimpleTree];
			} else {
				groupTree.value = [];
			}
		} catch (error) {
			console.error('Error fetching group tree:', error);
		}
	};
	/**
	 *    * Adds a media item to a group.
	 *    * @param groupId The ID of the group to add the media to.
	 *    * @param mediaId The ID of the media to add to the group.
	 *    * @returns A promise that resolves when the API call is complete.
	 *    */
	const addMediaToGroup = async (groupId: number, mediaId: number): Promise<void> => {
		const mutation = `
		mutation ($groupId: Int!, $mediaId: Int!) {
			GroupMutations {
				addMedia(groupId: $groupId, mediaId: $mediaId)
			}
		}`;
		try {
			const data = await sendGraphQLQuery<{ GroupMutations: { addMedia: boolean } }>(mutation, { groupId, mediaId });
			if (!data?.GroupMutations?.addMedia) {
				throw new Error('Failed to add media to group');
			}
		} catch (error) {
			console.error('Error adding media to group:', error);
			throw error;
		}
	};
	const getGroupTree = async (vhost: string): Promise<void> => {
		const query = `
		query (
			$vhost: Int
			$moderationStatus: String
			$parentGroup: Int
		) {
			Groups {
				groupTree(
					vhost: $vhost
					moderationStatus: $moderationStatus
					parentGroup: $parentGroup
				) {
					id
					name
					description
					treeleft
					treeright
					vhost
					mediaCount
					memberCount
					moderationStatus
					parentGroup
					children {
						id
						name
						description
						treeleft
						treeright
						vhost
						mediaCount
						memberCount
						moderationStatus
						parentGroup
						children {
							id
							name
							description
							treeleft
							treeright
							vhost
							mediaCount
							memberCount
							moderationStatus
							parentGroup
							children {
								id
								name
								description
								treeleft
								treeright
								vhost
								mediaCount
								memberCount
								moderationStatus
								parentGroup
								children {
									id
									name
									description
									treeleft
									treeright
									vhost
									mediaCount
									memberCount
									moderationStatus
									parentGroup
									children {
										id
										name
										description
										treeleft
										treeright
										vhost
										mediaCount
										memberCount
										moderationStatus
										parentGroup
										children {
											id
											name
											description
											treeleft
											treeright
											vhost
											mediaCount
											memberCount
											moderationStatus
											parentGroup
										}
									}
								}
							}
						}
					}
				}
			}
		}
		`;

		try {
			const data = await sendGraphQLQuery(query, {
				vhost: +vhost,
			});

			if (data?.Groups?.groupTree) {
				groupTree.value = [...data.Groups.groupTree];
			} else {
				console.error('Unexpected response structure:', data);
				groupTree.value = [];
			}
		} catch (error) {
			console.error('Error fetching channel tree:', error);
			groupTree.value = [];
			throw error;
		}
	};
	const isReady = async (vhost: string): Promise<boolean> => {
		const query = ` query ( $vhost: Int) { Groups { isGroupReady( vhost: $vhost)}}`;
		try {
			const data = await sendGraphQLQuery(query, {
				vhost: +vhost,
			});

			if (data?.Groups?.isGroupReady) {
				return true;
			} else {
				return false
			}
		} catch (error) {
			throw error;
		}

	};
	const queueMovement = async (vhost: number,gid:number, parentGroup:number, leftSibling:number ): Promise<boolean> => {
		const query = ` mutation ( $vhost: Int,$groupId: Int, $parentGroup: Int, $leftSibling: Int ) { GroupMutations { moveGroup( vhost: $vhost, groupId: $groupId, parentGroup:$parentGroup, leftSibling: $leftSibling)}}`;
		try {
			const data = await sendGraphQLQuery(query, {
				vhost: +vhost,groupId:+gid, parentGroup: +parentGroup, leftSibling: +leftSibling
			});

			if (data?.GroupMutations?.moveGroup) {
				return true;
			} else {
				return false
			}
		} catch (error) {
			throw error;
		}

	};



	const getUserGroups = async (userId: number, vhost: number): Promise<Group[]> => {
		const query = `
		query ($vhost: Int, $uid: Int) {
			Groups {
				groupList(vhost: $vhost, uid: $uid) {
					id name description
				}
			}
		}`;
		const data = await sendGraphQLQuery<{ Groups: { groupList: Group[] } }>(query, { vhost, uid: userId });
		return data?.Groups?.groupList || [];
	};

	const getGroups = async (vhost: number): Promise<Group[]> => {
		const query = `
		query ($vhost: Int!) {
			Groups {
				groupList(vhost: $vhost) {
					id name description memberCount mediaCount created isProject subStatus startDate endDate
				}
			}
		}`;
		const data = await sendGraphQLQuery<{ Groups: { groupList: Group[] } }>(query, { vhost });
		return data?.Groups?.groupList || [];
	};

	const addUserToGroup = async (groupId: number, userId: number): Promise<boolean> => {
		const mutation = `
		mutation ($groupId: Int!, $userId: Int!) {
			GroupMutations {
				addMember(groupId: $groupId, userId: $userId)
			}
		}`;
		const data = await sendGraphQLQuery<{ GroupMutations: { addMember: boolean } }>(mutation, { groupId, userId });
		return data?.GroupMutations?.addMember ?? false;
	};

	const removeUserFromGroup = async (groupId: number, userId: number): Promise<boolean> => {
		const mutation = `
		mutation ($groupId: Int!, $userId: Int!) {
			GroupMutations {
				removeMember(groupId: $groupId, userId: $userId)
			}
		}`;
		const data = await sendGraphQLQuery<{ GroupMutations: { removeMember: boolean } }>(mutation, { groupId, userId });
		return data?.GroupMutations?.removeMember ?? false;
	};

	const getGroup = async (groupId: number): Promise<Group | null> => {
		const query = `
		query ($groupId: Int!) {
			Groups {
				group(groupId: $groupId) {
					id name description note vhost created createdBy
					mediaCount memberCount moderationStatus parentGroup
					url email address city state country postalcode
					geo_latitude geo_longitude startDate endDate
					groupType isProject subStatus
				}
			}
		}`;
		const data = await sendGraphQLQuery<{ Groups: { group: Group } }>(query, { groupId });
		return data?.Groups?.group || null;
	};

	const createGroup = async (vhost: number, name: string, additionalInfo: Record<string, string>): Promise<boolean> => {
		const mutation = `
		mutation ($vhost: Int!, $name: String!, $additionalInfo: String) {
			GroupMutations {
				createGroup(vhost: $vhost, name: $name, additionalInfo: $additionalInfo)
			}
		}`;
		const data = await sendGraphQLQuery<{ GroupMutations: { createGroup: number | boolean } }>(mutation, {
			vhost,
			name,
			additionalInfo: JSON.stringify(additionalInfo),
		});
		return !!data?.GroupMutations?.createGroup;
	};

	const updateGroup = async (groupId: number, groupInfo: Record<string, unknown>): Promise<boolean> => {
		const mutation = `
		mutation ($groupId: Int!, $groupInfo: String) {
			GroupMutations {
				updateGroup(groupId: $groupId, groupInfo: $groupInfo)
			}
		}`;
		const data = await sendGraphQLQuery<{ GroupMutations: { updateGroup: boolean } }>(mutation, {
			groupId,
			groupInfo: JSON.stringify(groupInfo),
		});
		return !!data?.GroupMutations?.updateGroup;
	};

	return {
		groupTree,
		isReady,
		queueMovement,
		folderSort,
		folderOrder,
		addMediaToGroup,
		getGroupTree,
		getSimpleGroupTree,
		getUserGroups,
		getGroups,
		getGroup,
		createGroup,
		updateGroup,
		addUserToGroup,
		removeUserFromGroup,
	};
}

